import { useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEditingForm,
  selectUIStates,
  setEditingForm,
  setEditingQuestionId,
  setUIStates,
} from "../../redux/features/appData/appDataSlice";
import "./FormQuestionMenu.css";
import "../../css/Icons.css";
import { FormQuestionDeleteModal } from "./FormQuestionDeleteModal";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { apiCreateQuestionForForm, apiDeleteQuestion, apiUpdateQuestionConditions } from "../../services/QuestionService";
import { CreateQuestionForFormPayload, FormChanges, FormExtended, Question, UpdateFormChangesPayload, UpdateFormQuestionOrderPayload, UpdateQuestionConditionsPayload } from "effno-core";
import { apiFormHasEdits, apiUpdateFormChanges, apiUpdateFormQuestionOrder } from "../../services/FormService";
import { addNewQuestionToOrder } from "../shared/uiHelpers";
import { Condition } from "../workflow/questionWorkflowConditions";
import { isEmptyJSON } from "../../utils/utils";

const QUESTION_MENU = {
  DELETE: "delete",
  MOVE_UP: "end_up",
  MOVE_DOWN: "end_down",
  COPY: "copy",
  PREVIEW: "preview",
};


type FormQuestionMenuProps = {
    questionId: string;
    isEndType: boolean;
    onDelete?: (questionId: string) => void;
};

export default function FormQuestionMenu({ questionId, isEndType, onDelete }: FormQuestionMenuProps) {
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const editingForm = useSelector(selectEditingForm);
  const uiStates = useSelector(selectUIStates);

  const handleButtonClick = (event: any) => {
    setAnchorEl(event.currentTarget);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuSelection = (selection: string) => {
    // close the menu
    setAnchorEl(null);

    // route it
    switch (selection) {
      case QUESTION_MENU.DELETE:
        handleDelete();
        break;
      case QUESTION_MENU.MOVE_UP:
        handleMoveUp();
        break;
      case QUESTION_MENU.MOVE_DOWN:
        handleMoveDown();
        break;
      case QUESTION_MENU.COPY:
        handleCopy();
        break;
      case QUESTION_MENU.PREVIEW:
        handlePreview();
        break;
      default:
    }
  };

  const handleCopy = () => {
    if (!editingForm?.questions) {
      return;
    }

    const question = editingForm.questions.find((q: Question) => q.id === questionId);

    if (!question) {
      toast("Invalid question", toastFail);
      return;
    }

    const newQuestion = { ...question };
    newQuestion.id = "";
    newQuestion.title = `${newQuestion.title} (copy)`;

    (async () => {
      const payload : CreateQuestionForFormPayload = {
        formId: editingForm.id,
        type: newQuestion.type,
        title: newQuestion.title,
        description: newQuestion.description,
        required: newQuestion.required,
        settings: newQuestion.settings,
        conditions: newQuestion.conditions,
      };

      const result = await apiCreateQuestionForForm(newQuestion);

      if (!result) {
        toast("Error copying question", toastFail);
        return;
      }

      const newForm = { ...editingForm };
      const newQuestions = new Array(...newForm.questions);
      newQuestions.push(result);

      newForm.questions = newQuestions;
      const newOrder = addNewQuestionToOrder(newQuestions, result, editingForm?.questionOrder);
      newForm.questionOrder = newOrder;

      await apiFormHasEdits(newForm.id);
      newForm.hasEdits = true;

      dispatch(setEditingForm(newForm));
      dispatch(setEditingQuestionId(result.id));

      const formPayload: UpdateFormQuestionOrderPayload = {
        formId: newForm.id,
        questionOrder: newOrder,
      };

      const updatedForm = await apiUpdateFormQuestionOrder(formPayload);

      if (!updatedForm) {
        toast("Error updating question order", toastFail);
        return;
      }

      toast("Question copied", toastSuccess);
    })();
  }

  const handleMoveUp = async() => {
    if (!editingForm?.questionOrder) {
      return;
    }

    const theOrder = JSON.parse(editingForm.questionOrder);
    const index = theOrder.findIndex((id: string) => id === questionId);

    if (index === -1) {
      toast("Invalid question", toastFail);
      return;
    }

    if (index === 0) {
      return;
    }

    const newOrder = new Array(...theOrder);
    const temp = newOrder[index];
    newOrder[index] = newOrder[index - 1];
    newOrder[index - 1] = temp;

    const newForm = { ...editingForm };
    newForm.questionOrder = JSON.stringify(newOrder);

    await apiFormHasEdits(newForm.id);
    newForm.hasEdits = true;

    dispatch(setEditingForm(newForm));
    updateFormQuestionOrder(newForm.questionOrder)
  }

  const handleMoveDown = async() => {
    if (!editingForm?.questionOrder) {
      return;
    }

    const theOrder = JSON.parse(editingForm.questionOrder);
    const index = theOrder.findIndex((id: string) => id === questionId);

    if (index === -1) {
      toast("Invalid question", toastFail);
      return;
    }

    if (index === theOrder.length - 1) {
      return;
    }

    const newOrder = new Array(...theOrder);
    const temp = newOrder[index];
    newOrder[index] = newOrder[index + 1];
    newOrder[index + 1] = temp;

    const newForm = { ...editingForm };
    newForm.questionOrder = JSON.stringify(newOrder);

    await apiFormHasEdits(newForm.id);
    newForm.hasEdits = true;
    
    dispatch(setEditingForm(newForm));
    updateFormQuestionOrder(newForm.questionOrder)
  }

  const updateFormQuestionOrder = (questionOrder: string) => {
    (async () => {
      if (editingForm) {

        const payload: UpdateFormQuestionOrderPayload = {
          formId: editingForm.id,
          questionOrder,
        };

        const updatedForm = await apiUpdateFormQuestionOrder(payload);

        if (!updatedForm) {
          toast("Error updating question order", toastFail);
          return;
        }
      }
    })();
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(questionId)
    }
  }

  const handlePreview = () => {
    dispatch(setUIStates({
        ...uiStates,
        viewingBuild: false,
        viewingDesign: false,
        viewingSettings: false,
        viewingPreview: true,
        viewingComments: false,
        viewingResponses: false,
        viewingShare: false,
        commentMode: false,
    }));
    navigate("/preview/" + editingForm?.id + "?question=" + questionId)
  }

  // get the order of the question
  let questionOrderIndex = -1;
  let highestNonEndIndex = -1;
  let lowestEndIndex = 1000000;
  let highestEndIndex = -1;
  const hasEndQuestions = editingForm?.questions?.some((q: Question) => q.type === "END");

  if (editingForm?.questionOrder) {
    const theOrder = JSON.parse(editingForm.questionOrder);
    const index = theOrder.findIndex((id: string) => id === questionId);

    if (index !== -1) {
      questionOrderIndex = index;
    }

    // what is the index of the last end type
    if (editingForm.questions) {
      editingForm.questions.filter((q: Question) => q.type === "END").forEach((endQ: Question) => {
        const index = theOrder.findIndex((id: string) => id === endQ.id);

        if (index === -1) {
          return;
        }

        if (index < lowestEndIndex) {
          lowestEndIndex = index;
        }

        if (index > highestEndIndex) {
          highestEndIndex = index;
        }
      })
    }

    // what is index of last non-end type
    if (editingForm.questions) {
      editingForm.questions.filter((q: Question) => q.type !== "END").forEach((nonEndQ: Question) => {
        const index = theOrder.findIndex((id: string) => id === nonEndQ.id);

        if (index === -1) {
          return;
        }

        if (index > highestNonEndIndex) {
          highestNonEndIndex = index;
        }
      })
    }
  }

  // console.log("questionOrderIndex: ", questionOrderIndex);
  // console.log("highestNonEndIndex: ", highestNonEndIndex);
  // console.log("lowestEndIndex: ", lowestEndIndex);
  // console.log("highestEndIndex: ", highestEndIndex);
  
  return (
    <>
      <div className="form-question-menu" onClick={handleButtonClick}>
        <div className="form-question-menu-image">
          <img className="icon-three-dots-vertical" alt="" />
        </div>
      </div>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}>
        {/* {!isEndType && questionOrderIndex > 0 && 
          <MenuItem onClick={() => handleMenuSelection(QUESTION_MENU.MOVE_UP)}>
            Move Up
          </MenuItem>
        }
        {!isEndType && questionOrderIndex < highestNonEndIndex &&
          <MenuItem onClick={() => handleMenuSelection(QUESTION_MENU.MOVE_DOWN)}>
            Move Down
          </MenuItem>
        }
        {isEndType && questionOrderIndex !== lowestEndIndex &&
          <MenuItem onClick={() => handleMenuSelection(QUESTION_MENU.MOVE_UP)}>
            Move Up End
          </MenuItem>
        }
        {isEndType && questionOrderIndex !== highestEndIndex &&
          <MenuItem onClick={() => handleMenuSelection(QUESTION_MENU.MOVE_DOWN)}>
            Move Down End
          </MenuItem>
        } */}
        <MenuItem onClick={() => handleMenuSelection(QUESTION_MENU.COPY)}>
          Copy
        </MenuItem>

        {onDelete &&
          <MenuItem onClick={() => handleMenuSelection(QUESTION_MENU.DELETE)}>
            Delete
          </MenuItem>
        }

        <MenuItem onClick={() => handleMenuSelection(QUESTION_MENU.PREVIEW)}>
          Preview from here
        </MenuItem>
      </Menu>
    </>
  );
}

